import { db } from '@/db/client';
import { updatePlayerCacheFromEvents } from '@/db/player-events';
import { addSessionEvent } from '@/db/session-events';
import type { UserId } from '@/schemas';
import { parsePlayerId } from '@/schemas/ids';

/**
 * Merge unauthenticated content (entities without userId) to the authenticated user
 * Called when a user authenticates to claim ownership of guest-created content
 */
export async function mergeUnauthenticatedContent(userId: UserId): Promise<void> {
  if (!userId) {
    throw new Error('userId is required for mergeUnauthenticatedContent');
  }

  // 1. Merge players without userId
  await db.transaction('rw', db.players, db.playerEvents, async () => {
    const playersWithoutUserId = await db.players
      .filter((p) => !p.userId && !p.deletedAt)
      .toArray();
    for (const player of playersWithoutUserId) {
      // Update player events to include userId
      const playerEvents = await db.playerEvents
        .where('payload.playerId')
        .equals(player.id)
        .filter((e) => !e.userId)
        .toArray();

      for (const event of playerEvents) {
        await db.playerEvents.update(event.id, { userId });
      }

      // Update player cache
      await db.players.update(player.id, { userId });

      // Rebuild player cache from events
      try {
        await updatePlayerCacheFromEvents(parsePlayerId(player.id));
      } catch (error) {
        console.warn(`Failed to rebuild player cache for ${player.id}:`, error);
      }
    }
  });

  // 2. Merge team presets without userId
  await db.transaction('rw', db.teamPresets, async () => {
    const teamPresetsWithoutUserId = await db.teamPresets
      .filter((p) => !p.userId && !p.deletedAt)
      .toArray();
    for (const preset of teamPresetsWithoutUserId) {
      await db.teamPresets.update(preset.id, { userId });
    }
  });

  // 3. Merge sessions without userId
  const sessionsWithoutUserId = await db.sessionMetadata
    .filter((s) => !s.userId && !s.deletedAt)
    .toArray();
  for (const sessionMeta of sessionsWithoutUserId) {
    try {
      await addSessionEvent({
        sessionId: sessionMeta.id,
        type: 'SessionOwnershipClaimed',
        userId,
        payload: {
          claimedAt: Date.now(),
          previousOwner: undefined,
        },
      });
    } catch (error) {
      console.warn(`Failed to claim ownership for session ${sessionMeta.id}:`, error);
    }
  }
}
